import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Space, Modal, Form, Input, 
  Switch, message, Popconfirm, Typography, Divider, Tabs 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { 
  getAllWxPusherConfigs, 
  getWxPusherConfigById, 
  addWxPusherConfig, 
  updateWxPusherConfig, 
  deleteWxPusherConfig,
  getWxPusherSystemConfig,
  updateWxPusherSystemConfig
} from '../services/wxPusherService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text, Link } = Typography;
const { TabPane } = Tabs;

const WxPusherConfig = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [configList, setConfigList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [form] = Form.useForm();
  const [systemConfig, setSystemConfig] = useState({
    wx_pusher_enabled: false
  });

  // 加载微信推送配置列表
  const loadConfigList = async () => {
    setLoading(true);
    try {
      const data = await getAllWxPusherConfigs();
      setConfigList(data || []);
    } catch (error) {
      console.error('加载微信推送配置失败:', error);
      message.error('加载微信推送配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载系统配置
  const loadSystemConfig = async () => {
    try {
      const data = await getWxPusherSystemConfig();
      setSystemConfig({
        wx_pusher_enabled: data.wx_pusher_enabled === 'true'
      });
    } catch (error) {
      console.error('加载系统配置失败:', error);
      message.error('加载系统配置失败');
    }
  };

  // 更新系统配置
  const updateSystemConfig = async (key, value) => {
    try {
      await updateWxPusherSystemConfig(key, String(value));
      message.success('系统配置更新成功');
      loadSystemConfig();
    } catch (error) {
      console.error('更新系统配置失败:', error);
      message.error('更新系统配置失败');
    }
  };

  // 初始化加载
  useEffect(() => {
    loadConfigList();
    loadSystemConfig();
  }, []);

  // 打开添加/编辑模态框
  const showModal = (config = null) => {
    setEditingConfig(config);
    form.resetFields();
    
    if (config) {
      form.setFieldsValue({
        name: config.name,
        spt: config.spt,
        test_spt: config.test_spt,
        description: config.description,
        is_active: config.is_active === 1
      });
    }
    
    setModalVisible(true);
  };

  // 关闭模态框
  const handleCancel = () => {
    setModalVisible(false);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingConfig) {
        // 更新配置
        await updateWxPusherConfig(editingConfig.id, {
          name: values.name,
          spt: values.spt,
          testSpt: values.test_spt,
          description: values.description,
          isActive: values.is_active
        });
        message.success('更新配置成功');
      } else {
        // 添加配置
        await addWxPusherConfig({
          name: values.name,
          spt: values.spt,
          testSpt: values.test_spt,
          description: values.description,
          isActive: values.is_active
        });
        message.success('添加配置成功');
      }
      
      setModalVisible(false);
      loadConfigList();
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    }
  };

  // 删除配置
  const handleDelete = async (id) => {
    try {
      await deleteWxPusherConfig(id);
      message.success('删除配置成功');
      loadConfigList();
    } catch (error) {
      console.error('删除配置失败:', error);
      message.error('删除配置失败');
    }
  };

  // 切换微信推送启用状态
  const toggleWxPusherEnabled = (checked) => {
    updateSystemConfig('wx_pusher_enabled', checked);
  };

  // 表格列定义
  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'SPT',
      dataIndex: 'spt',
      key: 'spt',
      render: (text) => (
        <Text copyable ellipsis style={{ maxWidth: 200 }}>{text}</Text>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active) => (
        <span style={{ color: is_active ? 'green' : 'red' }}>
          {is_active ? '启用' : '禁用'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个配置吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 检查是否为管理员
  if (!isAdmin()) {
    return (
      <Card>
        <Title level={4}>无权访问</Title>
        <Paragraph>您没有权限访问此页面，请联系管理员。</Paragraph>
      </Card>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Card title="微信推送配置" style={{ marginBottom: 20 }}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="推送配置" key="1">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={() => showModal()}
                >
                  添加配置
                </Button>
                <Button onClick={loadConfigList}>刷新</Button>
                <Button 
                  type="primary" 
                  icon={<SettingOutlined />} 
                  onClick={() => navigate('/wx-templates')}
                  style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                >
                  消息模板管理
                </Button>
              </Space>
            </div>
            
            <Table 
              columns={columns} 
              dataSource={configList.map(item => ({ ...item, key: item.id }))} 
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          
          <TabPane tab="系统设置" key="2">
            <Card>
              <Form layout="vertical">
                <Form.Item
                  label="启用微信推送"
                  extra="启用后，新提交的运单将自动推送到微信"
                >
                  <Switch 
                    checked={systemConfig.wx_pusher_enabled} 
                    onChange={toggleWxPusherEnabled}
                  />
                </Form.Item>
              </Form>
            </Card>
            
            <Divider />
            
            <Card title="使用说明">
              <Paragraph>
                <Text strong>WxPusher 是一个开放的消息推送平台，可以将消息推送到微信。</Text>
              </Paragraph>
              
              <Paragraph>
                <Text>1. 首先需要关注 WxPusher 公众号，获取 SPT (Simple Push Token)</Text>
              </Paragraph>
              
              <Paragraph>
                <Text>2. 在"推送配置"选项卡中添加 SPT</Text>
              </Paragraph>
              
              <Paragraph>
                <Text>3. 在"系统设置"选项卡中启用微信推送功能</Text>
              </Paragraph>
              
              <Paragraph>
                <Text>4. 启用后，每当有新运单提交，系统会自动推送通知到微信</Text>
              </Paragraph>
              
              <Paragraph>
                <Link href="https://wxpusher.zjiecode.com" target="_blank">
                  访问 WxPusher 官网了解更多
                </Link>
              </Paragraph>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
      
      {/* 添加/编辑配置模态框 */}
      <Modal
        title={editingConfig ? '编辑配置' : '添加配置'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="例如：管理员推送" />
          </Form.Item>
          
          <Form.Item
            name="spt"
            label="SPT (Simple Push Token)"
            rules={[{ required: true, message: '请输入SPT' }]}
            extra="从WxPusher获取的推送令牌"
          >
            <Input placeholder="SPT_xxxxxxxx" />
          </Form.Item>
          
          <Form.Item
            name="test_spt"
            label="测试SPT"
            extra="用于测试模板推送的SPT，可以与正式推送目标不同"
          >
            <Input placeholder="请输入测试SPT（可选）" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea placeholder="可选，添加描述信息" />
          </Form.Item>
          
          <Form.Item
            name="is_active"
            label="启用状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WxPusherConfig;
